import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileSpreadsheet, PlayCircle, CheckCircle2, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DataImport() {
  const [items, setItems] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/imports"); setItems(data.items); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const text = await f.text(); setFileName(f.name); setCsvText(text); setBatch(null);
    try { const { data } = await api.post("/imports/people/analyze", { filename: f.name, csv_text: text }); setAnalysis(data); setMapping(data.suggested_mapping); }
    catch (e2) { toast.error(apiError(e2)); }
  };

  const runDryRun = async () => {
    if (!csvText) return toast.error("Choose a CSV file first");
    setBusy(true);
    try { const { data } = await api.post("/imports/people/dry-run", { filename: fileName, csv_text: csvText, source_system: "CSV Import", mapping }); setBatch(data); setAnalysis(null); load(); toast.success("Dry run complete — review below"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openBatch = async (id) => { try { const { data } = await api.get(`/imports/${id}`); setBatch(data); } catch (e) { toast.error(apiError(e)); } };
  const commit = async () => { setBusy(true); try { const { data } = await api.post(`/imports/${batch.id}/commit`); toast.success(`Imported ${data.results.created}, matched ${data.results.matched}`); openBatch(batch.id); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const rollback = async () => { setBusy(true); try { const { data } = await api.post(`/imports/${batch.id}/rollback`); toast.success(`Rolled back ${data.removed}`); openBatch(batch.id); load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };

  if (!items) return <PageLoader />;
  const r = batch?.report;

  return (
    <div>
      <PageHeader title="Imports & Migration" description="Safely import legacy People from CSV with dry-run validation, duplicate checks and rollback."
        actions={<a href="/admin/prime-scores" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50" data-testid="data-import-prime-link"><FileSpreadsheet className="w-4 h-4 text-brand" />Import Prime Scores</a>} />

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm mb-6">
        <h3 className="font-display font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2"><UploadCloud className="w-4 h-4 text-brand" /> New People Import</h3>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid="import-file-label">
            <FileSpreadsheet className="w-4 h-4 text-slate-500" /> {fileName || "Choose CSV file"}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </label>
          <Button onClick={runDryRun} disabled={busy || !csvText} className="bg-brand hover:bg-brand-strong" data-testid="import-dryrun"><PlayCircle className="w-4 h-4 mr-1.5" /> Dry Run</Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Dry run never creates or modifies People. Review the report before committing.</p>
      </div>

      {analysis && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm mb-6" data-testid="mapping-review">
          <h3 className="font-display font-semibold text-slate-900 text-sm mb-1">Review Column Mapping</h3>
          <p className="text-xs text-slate-400 mb-4">{analysis.row_count} rows · {analysis.headers.length} columns. Adjust any mapping, then run the dry run.</p>
          <div className="grid sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto tr-scroll">
            {analysis.headers.map((h) => (
              <div key={h} className="flex items-center gap-2" data-testid={`map-row-${h}`}>
                <span className="text-sm text-slate-600 w-40 truncate font-mono text-xs">{h}</span>
                <Select value={mapping[h]} onValueChange={(v) => setMapping({ ...mapping, [h]: v })}>
                  <SelectTrigger className="h-9" data-testid={`map-select-${h}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {analysis.targets.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {batch && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm mb-6" data-testid="dryrun-report">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div><h3 className="font-display font-semibold text-slate-900">{batch.human_id} · {batch.file_name}</h3>
              <p className="text-xs text-slate-400">{batch.source_system} · uploaded by {batch.uploaded_by_name}</p></div>
            <div className="flex items-center gap-2">
              <StatusBadge status={batch.status === "completed" ? "active" : batch.status === "rolled_back" ? "disabled" : "pending"} label={batch.status?.replace(/_/g, " ")} />
              {batch.status === "ready_for_review" && <Button onClick={commit} disabled={busy} className="bg-brand hover:bg-brand-strong" data-testid="import-commit"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm & Import</Button>}
              {(batch.status === "completed" || batch.status === "completed_with_warnings") && <Button variant="outline" onClick={rollback} disabled={busy} data-testid="import-rollback"><RotateCcw className="w-4 h-4 mr-1.5" /> Roll Back</Button>}
            </div>
          </div>
          {r && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                {[["Rows", r.totals.rows], ["Clean", r.totals.clean], ["Warnings", r.totals.warnings],
                  ["Errors", r.totals.errors], ["Will Create", r.totals.would_create], ["Will Match", r.totals.would_match]].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-slate-100 p-3 text-center" data-testid={`stat-${k.toLowerCase().replace(/ /g,'-')}`}>
                    <p className="text-2xl font-display font-bold text-slate-900">{v}</p><p className="text-xs text-slate-500">{k}</p></div>
                ))}
              </div>
              {batch.results && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 mb-4" data-testid="import-results">
                  Created {batch.results.created} · Matched {batch.results.matched} · Skipped {batch.results.skipped} · Classifications {batch.results.classifications_added} · Agent Profiles {batch.results.agent_profiles_created} · Legacy Notes {batch.results.legacy_notes_created}
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3 text-sm mb-5">
                <Info label="Invalid states (blanked, kept in raw)" value={r.invalid_states} />
                <Info label="Invalid emails" value={r.invalid_emails} />
                <Info label="Malformed URLs" value={r.invalid_urls} />
                <Info label="Agent Profiles to create" value={r.agent_profiles} />
                <Info label="Legacy Notes to create" value={r.notes_to_create} />
                <Info label="Name-only duplicate warnings" value={r.name_only_duplicates.length} />
              </div>
              {r.sources_to_create.length > 0 && <p className="text-sm text-slate-600 mb-3">Sources to create: {r.sources_to_create.map((s) => <span key={s} className="inline-block bg-slate-100 rounded px-2 py-0.5 mr-1.5 text-xs">{s}</span>)}</p>}
              <h4 className="font-display font-semibold text-sm text-slate-800 mb-2">Preview (normalization)</h4>
              <div className="overflow-x-auto tr-scroll rounded-lg border border-slate-200" data-testid="import-preview">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <th className="px-3 py-2">Row</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">State</th>
                    <th className="px-3 py-2">Classifications</th><th className="px-3 py-2">Comm Prefs</th><th className="px-3 py-2">Action</th><th className="px-3 py-2">Notes</th></tr></thead>
                  <tbody>
                    {r.preview.map((p) => (
                      <tr key={p.row} className="border-t border-slate-100" data-testid={`preview-row-${p.row}`}>
                        <td className="px-3 py-2 text-slate-400">{p.row}</td>
                        <td className="px-3 py-2 font-medium text-slate-800">{p.normalized.name || "—"}</td>
                        <td className="px-3 py-2">{p.original.state !== p.normalized.state ? <span className="text-slate-500">{p.original.state || "—"} → <b className="text-slate-800">{p.normalized.state || "—"}</b></span> : (p.normalized.state || "—")}</td>
                        <td className="px-3 py-2">{(p.normalized.classifications || []).map((c) => <StatusBadge key={c} status={c} className="mr-1" />)}</td>
                        <td className="px-3 py-2 text-slate-600">{(p.normalized.comm_prefs || []).join(", ") || "—"}</td>
                        <td className="px-3 py-2"><StatusBadge status={p.status === "would_create" ? "active" : p.status === "would_match" ? "pending" : "disabled"} label={p.status.replace(/_/g, " ")} /></td>
                        <td className="px-3 py-2 text-xs text-amber-700">{p.messages.length ? <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {p.messages.length}</span> : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <h3 className="font-display font-semibold text-slate-900 text-sm mb-2">Import History</h3>
      {items.length ? (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="import-history">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <th className="px-4 py-2">Batch</th><th className="px-4 py-2">File</th><th className="px-4 py-2">Rows</th>
              <th className="px-4 py-2">Imported</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Uploaded</th></tr></thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} onClick={() => openBatch(b.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`import-row-${b.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{b.human_id}</td><td className="px-4 py-2">{b.file_name}</td>
                  <td className="px-4 py-2">{b.total_rows}</td><td className="px-4 py-2">{b.imported_rows}</td>
                  <td className="px-4 py-2"><StatusBadge status={b.status === "completed" ? "active" : b.status === "rolled_back" ? "disabled" : "pending"} label={b.status?.replace(/_/g, " ")} /></td>
                  <td className="px-4 py-2 text-slate-500">{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState icon={FileSpreadsheet} title="No imports yet" description="Upload a CSV to run a dry run." />}
    </div>
  );
}

function Info({ label, value }) {
  return <div className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
    <span className="text-slate-600">{label}</span><span className="font-semibold text-slate-900">{value}</span></div>;
}
